import { formatCurrency } from '@packages/format';
import { z } from 'zod';
/**
 * Ratio tolerance for "is this an exact multiple of the increment". Shared by the
 * round-up and the multiple check so they cannot disagree: a duration the warning
 * logic calls an exact multiple must never be rounded up a full extra increment.
 */
const RATIO_TOLERANCE = 1e-6;
/**
 * Round units UP to the nearest billing increment.
 * Uses Math.ceil to always round up - attorneys are never rounded down.
 * Rounds to 10 decimal places to avoid floating point drift (e.g. 0.30000000000000004).
 *
 * The billing increment is an input-time steering aid, not a billing-time rule: this
 * is the single call site that ever rounds units to it, used at the moment `units` is
 * authored (the wizard's round-up action, calendar auto-creation). Once a
 * `ClientProjectActivity` row is written, its stored `amountMinorUnits` is the
 * financial source of truth and the increment is never consulted again - no reader
 * (invoice line builder, API response, WIP totals) re-applies it.
 *
 * The tolerance subtracted before the ceil is load-bearing: the FIVE_MIN increment
 * (1/12 hours) is not exactly representable in binary floats, so exact multiples of it
 * (25, 35, 50 minutes; and near-values like 0.1666666666666667 written by the AI and
 * calendar paths) can divide to a ratio a few ULPs ABOVE the integer. A bare ceil then
 * bills a full extra increment - real customer overbilling, not a display nit.
 */
export function applyTimeIncrement(units, increment) {
    // max(..., 0) keeps zero units at +0 (a bare ceil of -RATIO_TOLERANCE yields -0,
    // which Intl renders as "-0").
    const steps = Math.max(Math.ceil(units / increment - RATIO_TOLERANCE), 0);
    return Math.round(steps * increment * 1e10) / 1e10;
}
/**
 * Canonical list of supported billing time increments.
 *
 * Storage is a Float (hours). The enum key is the stable API identifier; the UI reads
 * `shortLabel` / `settingsLabel` so the raw float (e.g. 0.0833333...) is never rendered.
 * `FIVE_MIN` uses 1/12 hours, which is not exactly representable in binary floats -
 * `choiceFromHours` uses a tolerance match when reading it back, and `applyTimeIncrement`
 * absorbs the same drift at the one input-time site that rounds units to it.
 */
export const TIME_INCREMENT_CHOICES = {
    SIX_MIN: {
        hours: 1 / 10,
        minutes: 6,
        shortLabel: '6 min',
        settingsLabel: '6 minutes',
    },
    FIVE_MIN: {
        hours: 1 / 12,
        minutes: 5,
        shortLabel: '5 min',
        settingsLabel: '5 minutes',
    },
    THREE_MIN: {
        hours: 1 / 20,
        minutes: 3,
        shortLabel: '3 min',
        settingsLabel: '3 minutes',
    },
};
export const TimeIncrementChoiceSchema = z.enum(['SIX_MIN', 'FIVE_MIN', 'THREE_MIN']);
const HOURS_MATCH_TOLERANCE = 1e-6;
/**
 * Map a stored float back to its canonical enum choice. Returns `null` for `null` input or
 * any value that does not match a known choice within tolerance (shouldn't happen in
 * practice since writes go through `hoursFromChoice`, but guards against drift or legacy
 * values).
 */
export function choiceFromHours(hours) {
    if (hours == null)
        return null;
    for (const [key, value] of Object.entries(TIME_INCREMENT_CHOICES)) {
        if (Math.abs(value.hours - hours) < HOURS_MATCH_TOLERANCE)
            return key;
    }
    return null;
}
export function hoursFromChoice(choice) {
    return TIME_INCREMENT_CHOICES[choice].hours;
}
/**
 * Short inline label: "6 min" / "5 min" / "3 min" / "No rounding".
 * For hints embedded in other sentences (e.g. "rounded to 5 min").
 */
export function formatIncrementShort(hours) {
    if (hours == null)
        return 'No rounding';
    const choice = choiceFromHours(hours);
    if (choice)
        return TIME_INCREMENT_CHOICES[choice].shortLabel;
    return `${formatMinutesLabel(hours)}`;
}
/**
 * Verbose label for settings forms: "6 minutes (0.1 hours)" / "5 minutes" / etc.
 */
export function formatIncrementSettingsLabel(hours) {
    if (hours == null)
        return 'No rounding (free-form)';
    const choice = choiceFromHours(hours);
    if (choice)
        return TIME_INCREMENT_CHOICES[choice].settingsLabel;
    return formatMinutesLabel(hours);
}
/**
 * Convert hours to whole minutes. Rounds to absorb float drift (0.1 * 60 = 6.000...0001).
 * Matches Math.round semantics - use for display and form values only; storage stays in
 * hours.
 */
export function hoursToMinutes(hours) {
    return Math.round(hours * 60);
}
export function minutesToHours(minutes) {
    return minutes / 60;
}
/**
 * Render an hours value as a minutes label: "5 min", "6 min", "30 min". For
 * `defaultEmailTimeUnits` and any other free-form hours value where the user thinks in
 * minutes.
 */
export function formatMinutesLabel(hours) {
    return `${hoursToMinutes(hours)} min`;
}
/**
 * Returns true when `hours` is an integer multiple of `incrementHours` (within a 1e-6
 * tolerance that absorbs float drift on `1/12`-style increments).
 *
 * Zero hours is always a valid multiple. Zero or negative increment disables the check
 * (free-form rounding). Used by the service layer and by the settings form to enforce
 * that `defaultEmailTimeUnits` lines up with the configured `timeIncrement` - otherwise
 * every email bills rounded-up anyway, making the sub-increment stored value misleading.
 */
export function isHoursMultipleOfIncrement(hours, incrementHours) {
    if (hours === 0)
        return true;
    if (incrementHours == null || incrementHours <= 0)
        return true;
    const ratio = hours / incrementHours;
    return Math.abs(ratio - Math.round(ratio)) < RATIO_TOLERANCE;
}
/**
 * Render an hours value as a friendly duration in full words: "5 minutes", "1 hour",
 * "2 hours", "1 hour 30 minutes".
 *
 * Used everywhere a user-facing duration is rendered - activity cards, invoice line
 * items, PDF descriptions, diary views, chat context. Callers MUST only pass this
 * hours-valued durations; `unitType !== 'hours'` rows use their own unit label.
 *
 * Rounds to whole minutes to absorb float drift (e.g. `1/12 * 3 = 0.2500000000...`).
 * A duration of exactly zero is rendered as "0 minutes" rather than the empty string.
 */
export function formatDuration(hours) {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const minuteLabel = m === 1 ? 'minute' : 'minutes';
    const hourLabel = h === 1 ? 'hour' : 'hours';
    if (h === 0)
        return `${m} ${minuteLabel}`;
    if (m === 0)
        return `${h} ${hourLabel}`;
    return `${h} ${hourLabel} ${m} ${minuteLabel}`;
}
/**
 * Render an hours value as a compact duration for space-limited labels such as chat
 * buttons: "45 min", "1 hour", "5 hours", "1 hr 30 min". Whole hours keep the word so a
 * long entry never reads as "300 min"; a mixed duration abbreviates both parts to stay
 * short. Same minute rounding as `formatDuration`.
 */
export function formatDurationShort(hours) {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0)
        return `${m} min`;
    if (m === 0)
        return `${h} ${h === 1 ? 'hour' : 'hours'}`;
    return `${h} hr ${m} min`;
}
/**
 * Build a plain-English steering warning when `hours` is not a multiple of the
 * account's billing increment, or `null` when it already lines up (or the
 * account has no increment configured, per `isHoursMultipleOfIncrement`).
 *
 * Single source of truth for the warning text so the activity create/update
 * route and the CSV bulk-import row result cannot drift on wording. Carries
 * only a duration, an increment label, and a rate-derived amount - no PII - so
 * it is safe to surface on both the API response and a bulk-import row result.
 *
 * The amount uses the same `round(units x rate)` the service stores when a
 * duration is written, so an accepted round-up lands on exactly the quoted
 * figure.
 */
export function buildIncrementWarning(hours, incrementHours, rate) {
    if (incrementHours == null || incrementHours <= 0)
        return null;
    if (isHoursMultipleOfIncrement(hours, incrementHours))
        return null;
    const rounded = applyTimeIncrement(hours, incrementHours);
    const roundedAmount = rate?.ratePerUnitMinorUnits != null && rate.ratePerUnitMinorUnits > 0
        ? ` (${formatCurrency(Math.round(rounded * rate.ratePerUnitMinorUnits), rate.currency)} at the current rate)`
        : '';
    return `${formatDuration(hours)} is not a multiple of the ${formatIncrementShort(incrementHours)} billing increment. Round up to ${formatDuration(rounded)} to bill a whole increment${roundedAmount}.`;
}
